"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Server-side pagination controls with page-size selector and first/prev/next/last buttons.
 *
 * Pages are **1-based** (page 1 = first page).
 */
export function ServerPagination(t0) {
  const $ = _c(75);
  const {
    currentPage,
    totalPages,
    pageSize: t1,
    pageSizeOptions: t2,
    onPageChange,
    onPageSizeChange,
    className,
    layout: t3,
    middleSlot
  } = t0;
  const pageSize = t1 === undefined ? 10 : t1;
  const pageSizeOptions = t2 === undefined ? DEFAULT_PAGE_SIZE_OPTIONS : t2;
  const layout = t3 === undefined ? "default" : t3;
  const {
    t
  } = useTranslation();
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const safeTotalPages = Math.max(1, totalPages);
  let t4;
  if ($[0] !== onPageSizeChange || $[1] !== pageSize || $[2] !== pageSizeOptions || $[3] !== t) {
    t4 = onPageSizeChange ? <div className="text-muted-foreground hidden items-center gap-1.5 text-[11px] sm:flex"><Select value={String(pageSize)} onValueChange={value => onPageSizeChange(Number(value))}><SelectTrigger aria-label={t("common.table.pageSize", {
          defaultValue: "Items per page"
        })} className="h-6! w-16 py-0 text-[11px]" size="sm"><SelectValue /></SelectTrigger><SelectContent>{pageSizeOptions.map(_temp)}</SelectContent></Select></div> : <div className="hidden sm:block" />;
    $[0] = onPageSizeChange;
    $[1] = pageSize;
    $[2] = pageSizeOptions;
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const pageSizeSelect = t4;
  let t5;
  if ($[5] !== onPageChange) {
    t5 = () => onPageChange(1);
    $[5] = onPageChange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !canPrev;
  let t7;
  if ($[7] !== t) {
    t7 = t("common.table.firstPage");
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronsLeft className="size-3.5" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t9 = <Button type="button" variant="outline" size="icon" className="size-6" onClick={t5} disabled={t6} aria-label={t7}>{t8}</Button>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== currentPage || $[15] !== onPageChange) {
    t10 = () => onPageChange(currentPage - 1);
    $[14] = currentPage;
    $[15] = onPageChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !canPrev;
  let t12;
  if ($[17] !== t) {
    t12 = t("common.table.prevPage");
    $[17] = t;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronLeft className="size-3.5" />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t12) {
    t14 = <Button type="button" variant="outline" size="icon" className="size-6" onClick={t10} disabled={t11} aria-label={t12}>{t13}</Button>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== currentPage || $[25] !== safeTotalPages) {
    t15 = <span className="text-foreground px-1.5 text-[11px] font-medium tabular-nums">{currentPage} / {safeTotalPages}</span>;
    $[24] = currentPage;
    $[25] = safeTotalPages;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== currentPage || $[28] !== onPageChange) {
    t16 = () => onPageChange(currentPage + 1);
    $[27] = currentPage;
    $[28] = onPageChange;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = !canNext;
  let t18;
  if ($[30] !== t) {
    t18 = t("common.table.nextPage");
    $[30] = t;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronRight className="size-3.5" />;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t16 || $[34] !== t17 || $[35] !== t18) {
    t20 = <Button type="button" variant="outline" size="icon" className="size-6" onClick={t16} disabled={t17} aria-label={t18}>{t19}</Button>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== onPageChange || $[38] !== totalPages) {
    t21 = () => onPageChange(totalPages);
    $[37] = onPageChange;
    $[38] = totalPages;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = !canNext;
  let t23;
  if ($[40] !== t) {
    t23 = t("common.table.lastPage");
    $[40] = t;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ChevronsRight className="size-3.5" />;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t21 || $[44] !== t22 || $[45] !== t23) {
    t25 = <Button type="button" variant="outline" size="icon" className="size-6" onClick={t21} disabled={t22} aria-label={t23}>{t24}</Button>;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t14 || $[48] !== t15 || $[49] !== t20 || $[50] !== t25 || $[51] !== t9) {
    t26 = <div className="flex shrink-0 items-center gap-0.5">{t9}{t14}{t15}{t20}{t25}</div>;
    $[47] = t14;
    $[48] = t15;
    $[49] = t20;
    $[50] = t25;
    $[51] = t9;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const pageControls = t26;
  if (layout === "split") {
    const t27 = `grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3 ${className ?? ""}`;
    let t28;
    if ($[53] !== t27) {
      t28 = t27.trim();
      $[53] = t27;
      $[54] = t28;
    } else {
      t28 = $[54];
    }
    let t29;
    if ($[55] !== pageSizeSelect) {
      t29 = <div className="justify-self-start">{pageSizeSelect}</div>;
      $[55] = pageSizeSelect;
      $[56] = t29;
    } else {
      t29 = $[56];
    }
    let t30;
    if ($[57] !== middleSlot) {
      t30 = <div className="min-w-0 justify-self-center text-center">{middleSlot}</div>;
      $[57] = middleSlot;
      $[58] = t30;
    } else {
      t30 = $[58];
    }
    let t31;
    if ($[59] !== pageControls) {
      t31 = <div className="justify-self-end">{pageControls}</div>;
      $[59] = pageControls;
      $[60] = t31;
    } else {
      t31 = $[60];
    }
    let t32;
    if ($[61] !== t28 || $[62] !== t29 || $[63] !== t30 || $[64] !== t31) {
      t32 = <div className={t28}>{t29}{t30}{t31}</div>;
      $[61] = t28;
      $[62] = t29;
      $[63] = t30;
      $[64] = t31;
      $[65] = t32;
    } else {
      t32 = $[65];
    }
    return t32;
  }
  const t27 = `flex items-center justify-between gap-2 ${className ?? ""}`;
  let t28;
  if ($[66] !== t27) {
    t28 = t27.trim();
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== onPageSizeChange || $[69] !== pageSizeSelect) {
    t29 = onPageSizeChange ? pageSizeSelect : <div className="hidden sm:block" />;
    $[68] = onPageSizeChange;
    $[69] = pageSizeSelect;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== pageControls || $[72] !== t28 || $[73] !== t29) {
    t30 = <div className={t28}>{t29}{pageControls}</div>;
    $[71] = pageControls;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  return t30;
}
function _temp(size) {
  return <SelectItem key={size} value={String(size)}>{size}</SelectItem>;
}